/**
 * Feature columns - Figma `6377:88620` (and `6377:89189`).
 *
 * A heading over a three-column grid of bordered cards. Each card carries its title at
 * the top with a green icon badge beside it, and its body at the bottom, so the bodies
 * sit on a common baseline however long the titles run.
 *
 * **The badge glyph is a Heroicon, picked by name.** It used to be `panel.image`, because
 * when this was built there was no icon picker and an enumeration of the ~60 `icon/All`
 * glyphs would have been worse than letting an editor choose an asset. The visa mosaic
 * brought a picker, so the comment that said "if the catalogue sign-off brings an icon
 * picker, this becomes a name instead" has been honoured. `panel.image` still works and
 * wins when both are set, so nothing authored against the old field breaks.
 *
 * **The design's last cell is not built, and that is still the right call.** In
 * `6377:88620` and again in `3883:71161` the grid ends with a double-width panel carrying
 * a photograph, product logos and a button - a different component wearing this section's
 * grid. Placing a `promo-banner` after this section gets the content; what it does not get
 * is that cell sharing a row with the last card. Expressing that needs a width flag on
 * `shared.panel`, which `highlight-panel-list` also uses and reads differently, to buy one
 * cell on one page. That is the same trade the visa mosaic's double-width "Informacje
 * ogolne" tile was refused on, and it is refused here for the same reason.
 */
import { SectionShell, withHighlight } from '../primitives';
import { mediaUrl } from '../../lib/config';
import { HeroIcon } from '../../components/ui/HeroIcon';
import type { FeatureColumnsSection, Card } from '../../types/sections';

function Column({ panel }: { panel: Card }) {
  // 318px tall, 36px of padding, a 14px radius and a grey-500 border - every one of
  // those measured off 3883:71117 rather than picked. The height is a minimum, not a
  // fixture: a card is as tall as its longest neighbour, and Polish copy is longer than
  // the file's.
  return (
    <div className="flex min-h-[318px] flex-col justify-between gap-8 rounded-l border border-grey-500 bg-surface p-9">
      <div className="flex items-start justify-between gap-5">
        <h3 className="text-h5 text-ink-strong">{panel.title}</h3>
        {panel.image || panel.icon ? (
          <span className="grid h-[50px] w-[50px] shrink-0 place-items-center rounded-m bg-green-500 text-white">
            {panel.image ? (
              <img
                src={mediaUrl(panel.image.url)}
                alt=""
                loading="lazy"
                className="h-6 w-6 object-contain"
              />
            ) : (
              <HeroIcon name={panel.icon} size={24} />
            )}
          </span>
        ) : null}
      </div>

      {panel.body ? <p className="text-body text-ink-muted">{panel.body}</p> : null}
    </div>
  );
}

export default function FeatureColumns({ data }: { data: FeatureColumnsSection }) {
  const items = data.items ?? [];

  return (
    <SectionShell settings={data.settings} name="feature-columns">
      {/* Headers/h1 over 968 of the 1360 grid (3883:71114), with 56px to the grid. */}
      {data.heading ? (
        <h2 className="max-w-[968px] text-h3 text-ink lg:text-h1">{withHighlight(data.heading)}</h2>
      ) : null}

      {items.length ? (
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((panel, i) => <Column key={i} panel={panel} />)}
        </div>
      ) : null}
    </SectionShell>
  );
}
